import {
  Box,
  Typography,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DataObjectIcon from "@mui/icons-material/DataObject";
import SourceIcon from "./sourceIcon";
import { useState } from "react";

const Interface = ({ log }) => {
  const [copySuccess, setCopySuccess] = useState(false);

  // Function to format JSON data nicely
  const formatJsonData = (jsonData) => {
    console.log("formatJsonData", jsonData);
    
    if (!jsonData) return null;

    try {
      // If it's already a string, try to parse it
      const parsed =
        typeof jsonData === "string" ? JSON.parse(jsonData) : jsonData;
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      // If parsing fails, return as is
      return typeof jsonData === "string"
        ? jsonData
        : JSON.stringify(jsonData, null, 2);
    }
  };

  // Function to copy JSON to clipboard
  const copyToClipboard = async () => {
    try {
      const formattedJson = formatJsonData(log.details?.interface?.jsonData);
      await navigator.clipboard.writeText(formattedJson);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <Box dir="rtl">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          width: "100%",
        }}
      >
        {/* Header with Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "flex-start",
          }}
        >
          <Typography variant="body1">
            שגיאה אירעה בממשק:{" "}
            <strong>{log.details?.interface?.recipeName}</strong>
          </Typography>

          <Box sx={{ ml: 2 }}>
            <SourceIcon source={log.source} />
          </Box>
        </Box>

        {/* Details Section */}
        <Box
          sx={{
            display: "flex",
            width: "100%",
            mt: 1,
          }}
        >
          <Typography variant="body2" component="span">
            מקור: {log.details?.interface?.source}
          </Typography>
          <Typography variant="body2" component="span" sx={{ mx: 2 }}>
            | יעד: {log.details?.interface?.target}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            width: "100%",
            mt: 0.5,
          }}
        >
          <Typography variant="body2" component="span">
            קוד שגיאה: {log.details?.interface?.errorStatus}
          </Typography>
          <Typography variant="body2" component="span" sx={{ mx: 2 }}>
            | תאור שגיאה: {log.details?.errorDescription}
          </Typography>
        </Box>

        {/* Workato IDs Section */}
        {(log.details?.interface?.workatoJobId ||
          log.details?.interface?.workatoJobMasterId) && (
          <Box sx={{ width: "100%", mt: 0.5, pr: 2 }}>
            <Typography
              variant="body2"
              sx={{
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              {log.details.interface.workatoJobId &&
                `workato job id: ${log.details.interface.workatoJobId}`}
              {log.details.interface.workatoJobId &&
                log.details.interface.workatoJobMasterId &&
                " | "}
              {log.details.interface.workatoJobMasterId &&
                `workato job master id: ${log.details.interface.workatoJobMasterId}`}
            </Typography>
          </Box>
        )}

        {/* Workato URL */}
        <Box
          sx={{
            width: "100%",
            mt: 0.5,
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <Typography variant="body2" component="div">
            <Link
              href={log.details?.interface?.workatoUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "primary.main",
                textDecoration: "underline",
                "&:hover": {
                  textDecoration: "none",
                },
              }}
            >
              url workato
            </Link>
          </Typography>
        </Box>

        {/* JSON Data Section */}
        {log.details?.interface?.jsonData && (
          <Box sx={{ width: "100%", mt: 2 }}>
            <Accordion
              sx={{
                boxShadow: "none",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px 8px 0 0",
                  minHeight: "48px",
                  "&.Mui-expanded": {
                    minHeight: "48px",
                    borderRadius: "8px 8px 0 0",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                    "&.Mui-expanded": {
                      margin: "12px 0",
                    },
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DataObjectIcon sx={{ color: "#1976d2", fontSize: "20px" }} />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#1976d2" }}
                  >
                    נתוני JSON
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666", mr: 1 }}>
                    (לחץ לצפייה)
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 0 }}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #e0e0e0",
                    borderRadius: 0,
                    position: "relative",
                  }}
                >
                  {/* Copy Button */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      left: 8,
                      zIndex: 1,
                    }}
                  >
                    <Tooltip title={copySuccess ? "הועתק!" : "העתק JSON"}>
                      <IconButton
                        size="small"
                        onClick={copyToClipboard}
                        sx={{
                          backgroundColor: copySuccess ? "#4caf50" : "#fff",
                          color: copySuccess ? "#fff" : "#666",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          "&:hover": {
                            backgroundColor: copySuccess
                              ? "#4caf50"
                              : "#f0f0f0",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* JSON Content */}
                  <Box
                    sx={{
                      maxHeight: "400px",
                      overflow: "auto",
                      p: 2,
                      pr: 5, // Make room for copy button
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        fontSize: "12px",
                        lineHeight: 1.4,
                        margin: 0,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "#333",
                        direction: "ltr", // Force LTR for JSON readability
                        textAlign: "left",
                      }}
                    >
                      {formatJsonData(log.details?.interface?.jsonData)}
                    </Typography>
                  </Box>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Interface;